import { Alert, Button, Group, List, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { ConfirmActionModal } from "@how/ui";
import {
  type MatchDetails,
  type MatchOutcome,
  type SubmissionConfirmation,
  type SubmissionPreview,
  useApplySubmissionHeldChanges,
  useRecordSubmissionTextConsent,
  useResendFormConfirmation,
  useSubmissionConfirmation,
  useSubmissionPreview,
} from "./form-queries";

type Channel = NonNullable<MatchDetails["recorded"]>[number];

const CHANNEL_WORDS: Record<Channel, string> = {
  email: "email",
  sms: "text messages",
  phone: "phone calls",
  mail: "postal mail",
};

const COUNT_WORDS = ["no one", "one person", "two people", "three people", "four people"];
const people = (n: number | undefined) =>
  n === undefined ? "several people" : (COUNT_WORDS[n] ?? `${String(n)} people`);

const capitalized = (words: string) => words.charAt(0).toUpperCase() + words.slice(1);

/** Why a sign-up waits for staff, in the words staff read on it. */
export function needsYouTitle(details: MatchDetails): string {
  switch (details.reason) {
    case "several_match":
      return `Needs you: ${people(details.matchCount)} match`;
    case "name_differs":
      return "Needs you: the email or number belongs to someone with another name";
    case "same_name":
      return "Needs you: someone with this name has other contact details";
    case "child":
      return "Needs you: this matches a child";
    case "no_full_name":
      return "Needs you: the form gave no full name";
    case "nothing_to_match":
      return "Needs you: the form gave no email or number";
    case "no_email_to_confirm":
      return "Needs you: no email to confirm with";
    default:
      return "Needs you: this couldn't be matched";
  }
}

function needsYouBody(details: MatchDetails): string {
  switch (details.reason) {
    case "several_match":
      return `${capitalized(people(details.matchCount))} in your church have this email or number, so nothing was changed.`;
    case "name_differs":
      return "Someone in your church has this email or number under another first name, perhaps a family member, so nothing was changed.";
    case "same_name":
      return "Someone in your church has this name but a different email or number. It may be them with new details, or someone else, so nobody was added.";
    case "child":
      return "It matches a child in your church. A child's record and consent are changed only by staff, so nothing was changed.";
    case "no_full_name":
      return "A new person needs a first and a last name, so nobody was added.";
    case "nothing_to_match":
      return "There was nothing to match on, so nobody was added.";
    case "no_email_to_confirm":
      return "It matches someone in your church, but it gives details their record doesn't have, and their record has no email to send a confirmation to. Nothing was changed. If you know it's them, add the details yourself.";
    default:
      return "Nothing was changed.";
  }
}

export interface SubmissionMatchProps {
  readonly formId: string;
  readonly submissionId: string;
  readonly personId: string | null;
  readonly outcome: MatchOutcome | null;
  readonly details: MatchDetails;
}

/**
 * What happened when a sign-up arrived (#338 part 2, D22), in words: a decision staff still need to make, the consent
 * recorded, a yes to texts that waits and why, and a contact detail that differed and was left alone. "Matched to" and
 * "Added as a new person" are said beside the record's button, by SubmissionPerson. Nothing for a sign-up that was not
 * matched automatically (a form without a consent box, or one sent before matching existed).
 */
export function SubmissionMatch({
  formId,
  submissionId,
  personId,
  outcome,
  details,
}: SubmissionMatchProps) {
  if (outcome === null) return null;
  if (outcome === "awaiting_confirmation") {
    if (personId !== null) return null;
    return <AwaitingConfirmation formId={formId} submissionId={submissionId} details={details} />;
  }
  if (outcome === "needs_decision") {
    // Staff have decided since: SubmissionPerson says who it was added to.
    if (personId !== null) return null;
    return (
      <Alert color={semanticColor("warning")} variant="light" title={needsYouTitle(details)}>
        {needsYouBody(details)} Choose <strong>Add this person</strong> to decide.
      </Alert>
    );
  }
  return <MatchedNotes formId={formId} submissionId={submissionId} details={details} />;
}

function MatchedNotes({
  formId,
  submissionId,
  details,
}: {
  formId: string;
  submissionId: string;
  details: MatchDetails;
}) {
  const preview = useSubmissionPreview(submissionId);
  const forms = usePermission("forms.manage", { write: true });
  const preferences = usePermission("preferences.manage", { write: true });
  const record = useRecordSubmissionTextConsent();
  const data: SubmissionPreview | undefined = preview.data;
  const name = data?.personName ?? "this person";
  const recorded = details.recorded ?? [];
  const differs = details.differs ?? [];
  const waits = details.textConsentWaits;
  const canRecord = forms.data === true && preferences.data === true;

  const confirmedOn =
    details.confirmedAt === undefined ? "" : new Date(details.confirmedAt).toLocaleDateString();
  return (
    <Stack gap="xs">
      {details.confirmedBy === "email" ? (
        <Text size="sm">Confirmed by email on {confirmedOn}.</Text>
      ) : details.confirmedBy === "staff" ? (
        <Text size="sm">Details added by staff on {confirmedOn}.</Text>
      ) : null}
      <Text size="sm">
        {recorded.length === 0
          ? "No consent was recorded from this form."
          : `Recorded: yes to ${recorded.map((c) => CHANNEL_WORDS[c]).join(", ")}, from this form.`}
      </Text>
      {waits === "not_for_adults" ? (
        <Alert
          color={semanticColor("warning")}
          variant="light"
          title="Text consent waits: this form isn't marked for adults"
        >
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              Children are never texted, and this form doesn&apos;t say {name} is 18 or over. If you
              know they are, record their yes to texts.
            </Text>
            {record.isError ? (
              <Text size="sm" c="var(--mantine-color-error)">
                {describeRpcError(record.error).message}
              </Text>
            ) : null}
            {canRecord ? (
              <Button
                size="xs"
                variant="default"
                loading={record.isPending}
                onClick={() => {
                  record.mutate(
                    { formId, submissionId },
                    {
                      onSuccess: () => {
                        notifications.show({
                          color: semanticColor("success"),
                          title: "Recorded",
                          message: `${name} agreed to text messages, from this form.`,
                        });
                      },
                    },
                  );
                }}
              >
                Record their yes to texts
              </Button>
            ) : null}
          </Stack>
        </Alert>
      ) : waits === "not_main_number" ? (
        <Alert color={semanticColor("warning")} variant="light" title="Text consent waits">
          {name}&apos;s texts go to another number than the one on this form, so the yes to texts
          wasn&apos;t recorded. Open their record to check their main number.
        </Alert>
      ) : waits === "opted_out" ? (
        <Text size="sm">
          {name} texted STOP after this form, so its yes to text messages wasn&apos;t recorded.
        </Text>
      ) : waits === "no_number" ? (
        <Text size="sm">
          The form gave no number, so its yes to text messages wasn&apos;t recorded.
        </Text>
      ) : null}
      {(details.keptNewer ?? []).length > 0 ? (
        <Text size="sm">
          Not recorded: {name} answered about{" "}
          {(details.keptNewer ?? []).map((c) => CHANNEL_WORDS[c]).join(", ")} after this form, and
          that answer stays.
        </Text>
      ) : null}
      {differs.map((method) => {
        const given = method === "email" ? data?.email : data?.phone;
        const what = method === "email" ? "email" : "number";
        return (
          <Text key={method} size="sm">
            Not changed: the form gave the {what}
            {given === null || given === undefined ? "" : ` ${given}`}, which differs from the one
            on {name}&apos;s record. Update their record if it&apos;s new.
          </Text>
        );
      })}
    </Stack>
  );
}

type Held = NonNullable<MatchDetails["held"]>;

/** What waits, in the words the email uses, with the values the form gave. */
function heldWords(held: Held, preview: SubmissionPreview | undefined): string[] {
  const contacts = held.contacts.map((code) =>
    code === "email"
      ? `Add the email address${preview?.email ? ` ${preview.email}` : ""}`
      : code === "phone"
        ? `Add the mobile number${preview?.phone ? ` ${preview.phone}` : ""}`
        : "Add the number they texted from",
  );
  const consent = held.consent.map((c) => `Record their yes to ${CHANNEL_WORDS[c]}`);
  return [...contacts, ...consent];
}

function emailState(c: NonNullable<SubmissionConfirmation>, name: string): string {
  const sent = c.sentAt === null ? "" : ` (sent ${new Date(c.sentAt).toLocaleDateString()})`;
  if (c.status === "queued" || c.status === "sending") return "The email is on its way.";
  if (c.status === "failed") return "The email couldn't be sent. Try sending it again.";
  if (c.status === "blocked" && c.blockedReason === "test_address") {
    return `No email was sent: the address on ${name}'s record is a test address, which HoW never emails.`;
  }
  if (c.status === "blocked") return "No email could be sent.";
  if (c.expired) return `The link in the email${sent} has expired: links last seven days.`;
  return `The email${sent} asks ${name} to confirm. Its link lasts seven days.`;
}

/**
 * A sign-up that matched someone and gave details their record doesn't have (ADR 0063, as Planning Center): nothing
 * changes until they confirm by email. Staff see what waits, can send the email again, and can add the details
 * themselves when the person confirmed another way.
 */
function AwaitingConfirmation({
  formId,
  submissionId,
  details,
}: {
  formId: string;
  submissionId: string;
  details: MatchDetails;
}) {
  const confirmation = useSubmissionConfirmation(submissionId);
  const preview = useSubmissionPreview(submissionId);
  const forms = usePermission("forms.manage", { write: true });
  const people = usePermission("people.write", { write: true });
  const preferences = usePermission("preferences.manage", { write: true });
  const resend = useResendFormConfirmation();
  const apply = useApplySubmissionHeldChanges();
  const [confirming, setConfirming] = useState(false);
  const c = confirmation.data ?? null;
  const name = c?.personName ?? "the person";
  const sentOn = c?.sentAt ? ` (sent ${new Date(c.sentAt).toLocaleDateString()})` : "";
  const items = details.held === undefined ? [] : heldWords(details.held, preview.data);
  const canApply = forms.data === true && people.data === true && preferences.data === true;
  const sendAgainFrom = c?.sendAgainFrom ? new Date(c.sendAgainFrom) : null;
  const waitingToSend = c !== null && (c.status === "queued" || c.status === "sending");
  const tooSoon = sendAgainFrom !== null && sendAgainFrom.getTime() > Date.now();
  const canResend =
    forms.data === true && c !== null && !waitingToSend && c.sendsLeft > 0 && !tooSoon;

  return (
    <Alert
      color={semanticColor("info")}
      variant="light"
      title={`Waiting for ${name} to confirm by email${sentOn}`}
    >
      <Stack gap="xs" align="flex-start">
        <Text size="sm">
          This sign-up matches {name}, and gives details their record doesn&apos;t have. Nothing
          changes on their record until they confirm, so nobody can add their own number to someone
          else&apos;s record.
        </Text>
        {confirmation.isError ? (
          <Text size="sm" c="var(--mantine-color-error)">
            {describeRpcError(confirmation.error).message}
          </Text>
        ) : c !== null ? (
          <Text size="sm">{emailState(c, name)}</Text>
        ) : null}
        {items.length > 0 ? (
          <>
            <Text size="sm" fw={600}>
              Held until they confirm
            </Text>
            <List size="sm">
              {items.map((line) => (
                <List.Item key={line}>{line}</List.Item>
              ))}
            </List>
          </>
        ) : null}
        {resend.isError ? (
          <Text size="sm" c="var(--mantine-color-error)">
            {describeRpcError(resend.error).message}
          </Text>
        ) : null}
        {apply.isError ? (
          <Text size="sm" c="var(--mantine-color-error)">
            {describeRpcError(apply.error).message}
          </Text>
        ) : null}
        <Group gap="xs">
          {canResend ? (
            <Button
              size="xs"
              variant="default"
              loading={resend.isPending}
              onClick={() => {
                resend.mutate(
                  { submissionId },
                  {
                    onSuccess: () => {
                      notifications.show({
                        color: semanticColor("success"),
                        title: "Sending again",
                        message: `A new email is on its way to ${name}. The earlier link no longer works.`,
                      });
                    },
                  },
                );
              }}
            >
              Send again
            </Button>
          ) : null}
          {canApply ? (
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                setConfirming(true);
              }}
            >
              Add these details now
            </Button>
          ) : null}
        </Group>
        {c !== null && forms.data === true && !waitingToSend && !canResend ? (
          <Text size="xs" c="dimmed">
            {c.sendsLeft === 0
              ? "The email has been sent three times, the most HoW sends."
              : sendAgainFrom !== null
                ? `You can send it again from ${sendAgainFrom.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`
                : null}
          </Text>
        ) : null}
      </Stack>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={`Add these details to ${name}'s record?`}
        message={`Only do this if ${name} confirmed these details another way, for example in person or on the phone. The email's link will stop working.`}
        confirmLabel="Add these details"
        loading={apply.isPending}
        onConfirm={() => {
          apply.mutate(
            { formId, submissionId },
            {
              onSuccess: () => {
                setConfirming(false);
                notifications.show({
                  color: semanticColor("success"),
                  title: "Details added",
                  message: `${name}'s record now has the details from this sign-up.`,
                });
              },
            },
          );
        }}
      />
    </Alert>
  );
}
